import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  memo,
} from "react";
import clsx from "clsx";
import { MdDeleteOutline, MdOutlineAttachment } from "react-icons/md";
import { useTranslation } from "react-i18next";
import useMobile from "../../hooks/useMobile";
import {
  formatFileSize,
  getFileType,
  getMimeTypes,
  showNotification,
} from "src/helpers/utils";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import Cards from "../Cards/Cards";
import { parseOcrReceipt } from "src/api/openai";
import AlertDialog from "../AlertDialog/AlertDialog";
import CheckBox from "../CheckBox/CheckBox";
import useUiPreferences from "src/store/useUiPreferences";
import {
  FILE_TYPES_ATTACHMENTS,
  FILE_TYPES_IMAGES,
} from "src/constants/file-types";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind, IButton } from "../Button/Button.types";

export interface IFilePreview {
  file: File;
  name: string;
  size: number;
  formattedSize: string;
  type: string;
  preview: string;
  version: string;
}

export enum EAttachmentsTableViewStyle {
  TABLE = "table",
  GRID = "grid",
}

export interface IAttachmentsSelector {
  actions?: IButton[];
  onChange: (data: any) => void;
  savedAttachments: Array<any>;
  onClick: (data: any) => void;
  onOcrScan?: (data: any) => void;
}

const AttachmentsSelector: React.FC<IAttachmentsSelector> = ({
  actions = [],
  onChange,
  savedAttachments = [],
  onClick,
  onOcrScan,
}) => {
  const { t } = useTranslation(["common", "misc"]);
  const attachmentsUiPreferences = useUiPreferences((s) => s.attachments);
  const setUiPreferences = useUiPreferences((s) => s.setUiPreferences);

  const [isProcessingText, setIsProcessingText] = useState<boolean>(false);
  const [unsavedAttachments, setUnsavedAttachments] = useState<any[]>([]);
  const [showOpenAiError, setShowOpenAiError] = useState<boolean>(false);
  const [openAiErrorMessage, setOpenAiErrorMessage] = useState<string>("");
  const [deletedExistingAttachments, setDeletedExistingAttachments] = useState<
    any[]
  >([]);

  const isProcessingSomething = useMemo(() => {
    return [isProcessingText].includes(true);
  }, [isProcessingText]);

  const unifiedAttachmentsList = useMemo(() => {
    return [
      ...(savedAttachments?.map?.((theFile: any) => {
        return {
          name: theFile.originalName,
          size: theFile.size,
          formattedSize: formatFileSize(theFile.size),
          type: theFile.type,
          typeFormatted:
            theFile?.type?.split?.("/")?.[1]?.toUpperCase?.() ||
            t("misc:strings.unknown_file_type"),
          isSaved: true,
          preview: theFile.thumbnailUrl,
        };
      }) || []),
      ...(unsavedAttachments?.map?.((theFile: any) => {
        return {
          name: theFile.name,
          size: theFile.size,
          formattedSize: formatFileSize(theFile.size),
          type: theFile.type,
          typeFormatted:
            theFile?.type?.split?.("/")?.[1]?.toUpperCase?.() ||
            t("misc:strings.unknown_file_type"),
          isSaved: false,
          preview: theFile.preview,
        };
      }) || []),
    ];
  }, [savedAttachments, unsavedAttachments]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const ocrFileInputRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useMobile();
  const [dragging, setDragging] = useState(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    onChange({
      saved: savedAttachments,
      deleted: deletedExistingAttachments,
      unsaved: unsavedAttachments,
    });
  }, [unsavedAttachments, deletedExistingAttachments]);

  const totalFiles = useMemo(() => {
    return [...savedAttachments, ...unsavedAttachments].length;
  }, [savedAttachments, unsavedAttachments]);

  const handleFileChange = useCallback(
    (files: FileList | null) => {
      if (!files) return;

      const selectedFiles: IFilePreview[] = Array.from(files).map((file) => ({
        file,
        name: file.name,
        size: file.size,
        formattedSize: formatFileSize(file.size),
        type: file.type,
        formattedType: getFileType(file),
        preview: URL.createObjectURL(file),
        version: "v3",
      }));

      setUnsavedAttachments((prevFiles) => {
        const newFiles = selectedFiles.filter(
          (newFile) =>
            !prevFiles.some(
              (existingFile) =>
                existingFile.name === newFile.name &&
                existingFile.size === newFile.size,
            ),
        );

        const firstNewFileIndex = newFiles.length > 0 ? prevFiles.length : -1;

        if (attachmentsUiPreferences.autoPreview && firstNewFileIndex !== -1) {
          onClick({
            saved: false,
            index: firstNewFileIndex,
          });
        }

        return [...prevFiles, ...newFiles];
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [attachmentsUiPreferences.autoPreview],
  );

  const deleteCard = (data: any) => {
    const fileToDelete = `${data?.name}${data?.size}`;

    if (data?.isSaved) {
      const deletedAttachments = savedAttachments?.filter?.((saved: any) => {
        return fileToDelete === `${saved.originalName}${saved.size}`;
      });
      setDeletedExistingAttachments(deletedAttachments);
    } else {
      const newUnsaved = unsavedAttachments?.filter?.((unsaved: any) => {
        return fileToDelete !== `${unsaved.name}${unsaved.size}`;
      });
      setUnsavedAttachments(newUnsaved);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files) {
      setIsLoading(true);
      await handleFileChange(event.dataTransfer.files);
      setIsLoading(false);
    }
  };

  const showAttachmentsTable = useMemo(() => {
    const hasFiles = totalFiles > 0;

    return hasFiles;
  }, [totalFiles]);

  const hasFiles = useMemo(() => {
    return totalFiles > 0;
  }, [totalFiles]);

  const handleOcrParsing = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsProcessingText(true);
      handleFileChange(e.target.files);

      const resp = await parseOcrReceipt(file);

      if (resp?.data?.ok) {
        onOcrScan?.(resp.data.response);
      } else {
        if (resp?.data?.response?.code === "rate_limit_exceeded") {
          setOpenAiErrorMessage(resp?.data?.response?.error?.message);
          setShowOpenAiError(true);
        } else {
          showNotification({
            kind: "error",
            message: "Error escaneando la imagen",
          });
        }
        // console.log(resp);
      }
      setIsProcessingText(false);
    } catch (err) {
      console.log(err);
      showNotification({
        kind: "error",
        message: "Error escaneando la imagen",
      });
      setIsProcessingText(false);
    } finally {
      if (ocrFileInputRef.current) ocrFileInputRef.current.value = "";
    }
  };

  const acceptedOcrMimeTypes = getMimeTypes(FILE_TYPES_IMAGES);

  return (
    <>
      <div
        className={clsx(["flex", "w-full", "gap-4", "flex-col", "relative"])}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className={clsx(["flex", "w-full", "flex-col", "gap-4"])}>
          {showAttachmentsTable && (
            <>
              <Cards columns={2}>
                {unifiedAttachmentsList.map((attachment, index) => (
                  <Cards.FileCard
                    key={index}
                    name={attachment.name}
                    size={attachment.size}
                    type={attachment.type}
                    thumbnail={attachment.preview}
                    onClick={() => {
                      onClick({
                        saved: attachment?.isSaved,
                        index,
                      });
                    }}
                    buttons={[
                      {
                        icon: <MdDeleteOutline size={ICON_SIZES.SM} />,
                        onClick: () => deleteCard(attachment),
                        kind: EButtonKind.DANGER,
                        tooltip: t("common:tooltips.delete"),
                        disabled: isProcessingSomething,
                      },
                    ]}
                  />
                ))}
              </Cards>
            </>
          )}
          <div
            className={clsx([
              "flex",
              "w-full",
              "flex-col",
              "md:flex-row",
              "justify-center",
              "items-center",
              "md:items-center",
              "gap-4",
              "md:gap-6",
              "border-2",
              "border-dashed",
              "p-4",
              "md:p-4",
              // "cursor-pointer",
              "hover:border-red-500 hover:dark:border-purple-400/50",
              dragging
                ? "border-red-500 dark:border-gray-400"
                : "border-gray-200 dark:border-gray-600",
            ])}
            // onClick={() => openFileDialog()}
          >
            <div
              className={clsx([
                "flex",
                "w-full",
                "items-center",
                "md:items-start",
                "justify-center",
                "md:text-left",
                "flex-col",
              ])}
            >
              <div
                className={clsx([
                  "text-xs",
                  "md:text-sm",
                  "font-medium",
                  "md:text-left",
                ])}
              >
                {hasFiles ? (
                  <>{t("misc:attachments_selector.attach_more")}</>
                ) : (
                  <>{t("misc:attachments_selector.no_attachments")}</>
                )}
              </div>
              <p
                className={clsx([
                  "text-xs",
                  "md:text-sm",
                  "text-center",
                  "md:text-left",
                ])}
              >
                {t("misc:attachments_selector.message")}
              </p>
            </div>
            <div
              className={clsx(["flex", "flex-row", "gap-2", "flex-shrink-0"])}
            >
              <ButtonGroup
                buttons={[
                  {
                    text: t("misc:attachments_selector.buttons.select"),
                    icon: <MdOutlineAttachment size={ICON_SIZES.SM} />,
                    isLoading,
                    disabled: isLoading || isProcessingSomething,
                    onClick: (e: any) => {
                      e?.stopPropagation?.();
                      openFileDialog();
                    },
                  },
                  // {
                  //   tooltip: t("Escanear"),
                  //   text: "Escanear",
                  //   onClick: () => ocrFileInputRef.current?.click(),
                  //   icon: <MdOutlineDocumentScanner size={ICON_SIZES.SM} />,
                  //   isLoading: isProcessingSomething,
                  //   disabled: isProcessingSomething,
                  //   hide: !onOcrScan,
                  // },
                ]}
                align="center"
              />
            </div>
            <input
              type="file"
              multiple
              accept={getMimeTypes(FILE_TYPES_ATTACHMENTS)}
              ref={fileInputRef}
              onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                setIsLoading(true);

                await handleFileChange(e?.target?.files);
                setIsLoading(false);
              }}
              className="hidden"
            />
            <input
              ref={ocrFileInputRef}
              type="file"
              accept={acceptedOcrMimeTypes}
              className="hidden"
              onChange={handleOcrParsing}
            />
          </div>
          <div>
            <CheckBox
              onChange={(value: boolean) => {
                setUiPreferences({
                  attachments: {
                    autoPreview: value,
                  },
                });
              }}
              checked={attachmentsUiPreferences.autoPreview}
              description={t("misc:attachments_selector.auto_preview_message")}
              label={t("misc:attachments_selector.auto_preview_title")}
            />
          </div>
        </div>
      </div>
      {showOpenAiError && openAiErrorMessage && (
        <AlertDialog
          description={openAiErrorMessage}
          onClose={() => {
            setShowOpenAiError(false);
            setOpenAiErrorMessage("");
          }}
          title="Error de OpenAI"
        />
      )}
    </>
  );
};

export default memo(AttachmentsSelector);
